import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { useCompanyInfo } from '../hooks/useAPI';

const TermsOfServicePage = () => {
  const { data: companyInfo } = useCompanyInfo();
  const company = companyInfo?.companyName || 'LionTek';
  const email = companyInfo?.email || 'hello@liontekglobal.com';

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Terms of Service | LionTek</title>
        <meta name="description" content="LionTek's Terms of Service — the rules and guidelines governing use of our website and services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://liontekglobal.com/terms-of-service" />
      </Helmet>
      <Header companyInfo={companyInfo} />

      <main className="pt-24 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">

          {/* Header */}
          <div className="mb-10">
            <Link to="/" className="text-sm text-lion-blue hover:text-lion-yellow transition-colors mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-4xl font-poppins font-bold text-lion-blue mb-3">
              Terms of Service
            </h1>
            <p className="text-gray-500 text-sm">Last updated: March 2026</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <p>
                Please read these Terms of Service carefully before using the {company} website
                at <strong>liontekglobal.com</strong>. By accessing or using our website, you agree
                to be bound by these terms. If you do not agree, please do not use our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">1. Use of Website</h2>
              <p className="mb-3">You agree to use this website only for lawful purposes. You must not:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the site in any way that violates applicable local, national, or international laws or regulations.</li>
                <li>Transmit any unsolicited or unauthorised advertising or promotional material.</li>
                <li>Attempt to gain unauthorised access to any part of the website or its related systems.</li>
                <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the website.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">2. Intellectual Property</h2>
              <p className="mb-3">
                All content on this website — including text, graphics, logos, images, and software —
                is the property of {company} or its content suppliers and is protected by applicable
                intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, or create derivative works of any content
                without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">3. Our Services</h2>
              <p className="mb-3">
                {company} provides technology solutions including but not limited to ERP systems,
                AI/ML solutions, cybersecurity, digital marketing, and corporate training.
                Specific terms for contracted services are governed by separate service agreements.
              </p>
              <p>
                We reserve the right to modify, suspend, or discontinue any part of our website or
                services at any time without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">4. Disclaimer of Warranties</h2>
              <p className="mb-3">
                This website and its content are provided on an "as is" and "as available" basis
                without any warranties of any kind, either express or implied.
              </p>
              <p>
                {company} does not warrant that the website will be uninterrupted, error-free, or
                free of viruses or other harmful components.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">5. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by applicable law, {company} shall not be liable for
                any indirect, incidental, special, consequential, or punitive damages arising out of
                your access to or use of (or inability to access or use) the website or its content.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">6. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. These links are provided for
                your convenience only. {company} has no control over the content of those sites and
                accepts no responsibility for them or for any loss or damage that may arise from
                your use of them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">7. Privacy</h2>
              <p>
                Your use of this website is also governed by our{' '}
                <Link to="/privacy-policy" className="text-lion-blue hover:text-lion-yellow underline">
                  Privacy Policy
                </Link>
                , which is incorporated into these Terms by reference.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">8. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the
                Republic of Sierra Leone. Any disputes arising under these Terms shall be subject
                to the exclusive jurisdiction of the courts of Sierra Leone.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">9. Changes to These Terms</h2>
              <p>
                We reserve the right to update these Terms of Service at any time. Changes will be
                effective immediately upon posting to the website. Your continued use of the website
                after any changes constitutes your acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-poppins font-bold text-lion-blue mb-4">10. Contact Us</h2>
              <p>If you have any questions about these Terms, please contact us at:</p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lion-blue">{company}</p>
                <p>{companyInfo?.address || 'Africa'}</p>
                <p>
                  <a href={`mailto:${email}`} className="text-lion-blue hover:text-lion-yellow underline">
                    {email}
                  </a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer companyInfo={companyInfo} />
    </div>
  );
};

export default TermsOfServicePage;
